import { css } from '@emotion/core';
import styled, { CreateStyled } from '@emotion/styled';
import { merge } from 'lodash/fp';

import InterMedium from './assets/fonts/Inter/Inter-Medium.woff2';
import InterRegular from './assets/fonts/Inter/Inter-Regular.woff2';
import InterSemiBold from './assets/fonts/Inter/Inter-SemiBold.woff2';

export const styles = css`
  @font-face {
    font-family: Inter;
    font-style: normal;
    font-weight: 400;
    src: ${`url(${InterRegular}) format('truetype')`};
  }
  @font-face {
    font-family: Inter;
    font-style: normal;
    font-weight: 500;
    src: ${`url(${InterMedium}) format('truetype')`};
  }
  @font-face {
    font-family: Inter;
    font-style: normal;
    font-weight: 600;
    src: ${`url(${InterSemiBold}) format('truetype')`};
  }

  * {
    box-sizing: border-box;
    font-family: Inter;
    margin: 0;
    padding: 0;
  }

  html {
    font-size: 16px;
  }

  a {
    color: #000;
    text-decoration: none;
  }

  a:hover,
  a:active,
  a:focus {
    text-decoration: none;
  }
  .text-align--left {
    text-align: left;
  }
  .text-align--center {
    text-align: center;
  }
  .text-align--right {
    text-align: right;
  }
  @media (min-width: 992px) {
    .container {
      max-width: 964px;
    }
  }

  input[type='date']::-webkit-calendar-picker-indicator {
    -webkit-appearance: none;
    display: none;
  }

  input[type='date']::-webkit-inner-spin-button {
    -webkit-appearance: none;
    display: none;
  }

  .disabled-chart {
    background-color: #f6f6f6 !important;
    filter: grayscale(1);
  }

  .disabled-chart .chart-container {
    pointer-events: none;
    opacity: 0.2;
  }
`;

export const Theme: Theme = {
  borderRadius: '8px',
  buttonRadius: '15px',
  colors: {
    alertDanger: '#ff564d',
    alertGood: '#0eda83',
    alertInfo: '#3fa2fe',
    alertRisk: '#ffc600',
    backgroundGrey: '#fafaf7',
    backgroundGreyDarker: '#f2f2f2',
    black: '#000000',
    blue: '#4386d4',
    borderLight: '#e5e5e5',
    brand: '#ee6059',
    charcoal: '#343433',
    disabledGrey: 'rgba(0, 0, 0, 0.6)',
    disabledText: '#aeaeae',
    softGrey: '#d8d8d8',
    softWhite: '#fafafa',
    warmGrey: '#939393',
    white: '#ffffff',
  },
  fontSize: 12,
  spacing: {
    padding: {
      large: '1.072em',
      normal: '0.5em 0.75em',
      small: '0.25em 0.5em',
    },
  },
};

export const MobileTheme: Theme = merge(Theme, {
  borderRadius: '4px',
  buttonRadius: '15px',
  fontSize: 16,
  spacing: {
    padding: {
      large: '0.719em',
      normal: '0.344em 0.75em',
    },
  },
});

type alertDanger = '#ff564d';
type alertGood = '#0eda83';
type alertInfo = '#3fa2fe';
type alertRisk = '#ffc600';
type backgroundGrey = '#fafaf7';
type backgroundGreyDarker = '#f2f2f2';
type black = '#000000';
type blue = '#4386d4';
type borderLight = '#e5e5e5';
type brand = '#ee6059';
type charcoal = '#343433';
type disabledGrey = 'rgba(0, 0, 0, 0.6)';
type disabledText = '#aeaeae';
type softGrey = '#d8d8d8';
type softWhite = '#fafafa';
type warmGrey = '#939393';
type white = '#ffffff';

export type AppColorStrings =
  | 'alertDanger'
  | 'alertGood'
  | 'alertInfo'
  | 'alertRisk'
  | 'backgroundGrey'
  | 'backgroundGreyDarker'
  | 'black'
  | 'blue'
  | 'borderLight'
  | 'brand'
  | 'charcoal'
  | 'disabledGrey'
  | 'disabledText'
  | 'softGrey'
  | 'softWhite'
  | 'warmGrey'
  | 'white';

export type AppSpacingStrings = 'small' | 'normal' | 'large';
export type AppAlertColors = 'DANGER' | 'RISK' | 'GOOD';
export type AppColors =
  | alertGood
  | alertDanger
  | alertRisk
  | backgroundGrey
  | backgroundGreyDarker
  | black
  | blue
  | borderLight
  | alertInfo
  | brand
  | charcoal
  | disabledGrey
  | disabledText
  | softGrey
  | softWhite
  | warmGrey
  | white;

export interface Theme {
  readonly borderRadius: string;
  readonly buttonRadius: string;
  readonly colors: { readonly [key in AppColorStrings]: AppColors };
  readonly fontSize: number;
  readonly spacing: {
    readonly padding: { [k in AppSpacingStrings]: string };
  };
}

export interface HasColor {
  color: AppColorStrings;
}

export default styled as CreateStyled<Theme>;
